import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth.jsx";
import { Toaster } from "@/components/ui/sonner";

import Login from "@/pages/Login.jsx";
import AuthCallback from "@/pages/AuthCallback.jsx";
import AdminShell from "@/pages/admin/AdminShell.jsx";
import FormsList from "@/pages/admin/FormsList.jsx";
import FormBuilder from "@/pages/admin/FormBuilder.jsx";
import FormResponses from "@/pages/admin/FormResponses.jsx";
import Candidates from "@/pages/admin/Candidates.jsx";
import Outreach from "@/pages/admin/Outreach.jsx";
import Settings from "@/pages/admin/Settings.jsx";
import Dashboard from "@/pages/admin/Dashboard.jsx";
import NewJoiners from "@/pages/admin/NewJoiners.jsx";
import Checklists from "@/pages/admin/Checklists.jsx";
import Modules from "@/pages/admin/Modules.jsx";
import Tracks from "@/pages/admin/Tracks.jsx";
import TrainingProgress from "@/pages/admin/Progress.jsx";
import Employees from "@/pages/admin/Employees.jsx";
import Team from "@/pages/admin/Team.jsx";
import EmployeeProfile from "@/pages/admin/EmployeeProfile.jsx";
import Freelancers from "@/pages/admin/Freelancers.jsx";
import Leaves from "@/pages/admin/Leaves.jsx";
import Tickets from "@/pages/admin/Tickets.jsx";
import POSH from "@/pages/admin/POSH.jsx";
import Policies from "@/pages/admin/Policies.jsx";
import Digest from "@/pages/admin/Digest.jsx";
import HREmail from "@/pages/admin/HREmail.jsx";
import Portal from "@/pages/Portal.jsx";
import PublicForm from "@/pages/PublicForm.jsx";

function Protected({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A] text-[#71717A] font-mono text-xs">LOADING…</div>;
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  return children;
}

function RoleHome() {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "employee") return <Navigate to="/portal" replace />;
  if (user.role === "internal_committee") return <Navigate to="/admin/posh" replace />;
  return <Navigate to="/admin/dashboard" replace />;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/portal" element={<Protected><Portal /></Protected>} />

      <Route path="/admin" element={<Protected><AdminShell /></Protected>}>
        <Route index element={<RoleHome />} />
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="forms" element={<FormsList />} />
        <Route path="forms/new" element={<FormBuilder />} />
        <Route path="forms/:formId/edit" element={<FormBuilder />} />
        <Route path="forms/:formId/responses" element={<FormResponses />} />
        <Route path="candidates" element={<Candidates />} />
        <Route path="outreach" element={<Outreach />} />
        <Route path="onboarding/new-joiners" element={<NewJoiners />} />
        <Route path="onboarding/checklists" element={<Checklists />} />
        <Route path="training/modules" element={<Modules />} />
        <Route path="training/tracks" element={<Tracks />} />
        <Route path="training/progress" element={<TrainingProgress />} />
        <Route path="team" element={<Team />} />
        <Route path="team/:eid" element={<EmployeeProfile />} />
        <Route path="employees" element={<Employees />} />
        <Route path="freelancers" element={<Freelancers />} />
        <Route path="leaves" element={<Leaves />} />
        <Route path="tickets" element={<Tickets />} />
        <Route path="policies" element={<Policies />} />
        <Route path="digest" element={<Digest />} />
        <Route path="hr-email" element={<HREmail />} />
        <Route path="posh" element={<POSH />} />
        <Route path="settings" element={<Settings />} />
      </Route>

      <Route path="/" element={<RoleHome />} />
      <Route path="/:slug" element={<PublicForm />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <Toaster richColors position="top-right" theme="dark" />
      </AuthProvider>
    </BrowserRouter>
  );
}
